import type { MrpContributingProduct } from '@api/mrp';
import type { RateBasis } from '@api/inventory';
import type { DataSheetColumn } from '@components/ui/DataSheet';
import { formatMaterialQuantity } from '@utils/materialQuantity';

/** The text colours the planning tables use; kept here so the columns read the same on both pages. */
const text = {
    primary: 'rgb(26, 32, 44)',
    muted: 'rgb(148, 163, 184)',
};

/**
 * The products that consume one material, with how much of it each one pulls a day.
 *
 * Shown under a material on the Production Planning drill-in and on the material's own
 * page: the same rows the MRP plan computes, read the same way. `unit` is the material's
 * unit, which is what every quantity here is measured in; `basis` names the demand column
 * for what it is, since a forecast and a run-rate are different promises.
 */
export const contributingProductColumns = (
    unit: string,
    basis: RateBasis,
): DataSheetColumn<MrpContributingProduct>[] => [
    {
        key: 'productName',
        header: 'Product',
        width: 260,
        render: (val: string, row) => (
            <div className="flex flex-col">
                <span style={{ fontSize: 13, color: text.primary }}>{val}</span>
                <span style={{ fontSize: 11, color: text.muted }}>{row.productSku ?? row.productId}</span>
            </div>
        ),
    },
    {
        key: 'demandDailyUnits',
        header: basis === 'BACKWARD' ? 'DRR/day' : 'Forecast/day',
        width: 120,
        align: 'right' as const,
        render: (val: number) => (
            <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{val.toFixed(1)}</span>
        ),
    },
    {
        key: 'qtyPerFg',
        header: 'Qty per FG',
        width: 120,
        align: 'right' as const,
        render: (val: number) => (
            <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatMaterialQuantity(val, unit)}</span>
        ),
    },
    {
        /*
         * WHOSE BOM THIS NUMBER CAME FROM. A product two vendors make differently is planned
         * at the HIGHEST of them - buying for the mix would need a split nobody has stated,
         * and would under-buy the day the mix moved. Naming the vendor is what makes that
         * figure traceable: "why 40kg" has an answer the brand can open.
         */
        key: 'bomVendorName',
        header: 'Vendor',
        width: 150,
        render: (_: unknown, row) =>
            row.bomVariesByVendor ? (
                <span
                    style={{ fontSize: 12, color: text.primary }}
                    title="This product's vendors use different quantities here. The plan takes the highest."
                >
                    {row.bomVendorName || row.bomVendorId || 'All vendors'}
                    <span style={{ color: text.muted }}> (highest)</span>
                </span>
            ) : (
                <span style={{ fontSize: 12, color: text.muted }}>
                    {row.bomVendorId ? row.bomVendorName || row.bomVendorId : 'All vendors'}
                </span>
            ),
    },
    {
        key: 'contributionToDailyDemand',
        header: 'Material/day',
        width: 140,
        align: 'right' as const,
        render: (val: number) => (
            <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: text.primary }}>
                {formatMaterialQuantity(val, unit)}
            </span>
        ),
    },
];
